/**
 * UserMenu — the signed-in user in the top bar as a dropdown: name / email / role header,
 * dark-theme switch (+ "Use system theme"), Security settings, and Sign out (which clears the server caches through
 * the auth store and lands on /login).
 */
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, LogOut, Moon, ShieldCheck, Sun } from '../ui/icons.ts';
import { useState, type JSX } from 'react';

import { formatRole } from '../../lib/format/role.ts';
import { resolveTheme, useTheme } from '../../lib/theme.ts';
import { useAuth } from '../../store/auth-store.ts';
import { Switch } from '@argus/ui';
import { Menu, MenuItem, MenuSeparator } from '../ui/popover.tsx';

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('') || '?'
  );
}

export function UserMenu(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const identity = useAuth((s) => s.identity);
  const signOut = useAuth((s) => s.signOut);
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const name = user?.full_name ?? identity?.full_name ?? user?.email ?? identity?.email ?? '';
  const email = user?.email ?? identity?.email ?? '';
  const resolved = resolveTheme(theme);

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
    setOpen(false);
    await navigate({ to: '/login' });
  }

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      align="end"
      aria-label="Account"
      trigger={
        <button type="button" className="argus-user-menu" aria-label="Account menu">
          <span className="argus-user-menu__avatar" aria-hidden="true">
            {initialsOf(name)}
          </span>
          <span className="argus-user-menu__name">{name}</span>
          <ChevronDown size={14} aria-hidden="true" className="text-muted" />
        </button>
      }
    >
      <div className="argus-menu__header">
        <span className="argus-menu__primary">{name}</span>
        {email && email !== name ? <span className="argus-menu__secondary">{email}</span> : null}
        {user ? <span className="argus-menu__secondary">{formatRole(user.role)}</span> : null}
      </div>
      <MenuSeparator />
      {/* Dark theme switch. On = dark. Flipping it sets an explicit light/dark preference
          (so it works from the "system" state too, starting from what system resolves to);
          "Use system" returns the store to `system`. */}
      <Switch
        label="Dark theme"
        checked={resolved === 'dark'}
        stateText={resolved === 'dark' ? 'On' : 'Off'}
        icon={
          resolved === 'dark' ? (
            <Moon size={16} aria-hidden="true" />
          ) : (
            <Sun size={16} aria-hidden="true" />
          )
        }
        onCheckedChange={(on) => setTheme(on ? 'dark' : 'light')}
      />
      {theme !== 'system' ? (
        <MenuItem className="argus-menu__item--sub" onClick={() => setTheme('system')}>
          Use system theme
        </MenuItem>
      ) : null}
      <MenuItem
        icon={<ShieldCheck size={16} aria-hidden="true" />}
        onClick={() => {
          setOpen(false);
          void navigate({ to: '/settings/security' });
        }}
      >
        Security settings
      </MenuItem>
      <MenuSeparator />
      <MenuItem
        icon={<LogOut size={16} aria-hidden="true" />}
        disabled={signingOut}
        onClick={() => {
          void handleSignOut();
        }}
      >
        {signingOut ? 'Signing out…' : 'Sign out'}
      </MenuItem>
    </Menu>
  );
}
